import { faExternalLink, faSpinner, faTrash, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useCallback, useContext, useMemo, useState } from 'react';

import PersonInPlaceEditor from '@/components/PersonInPlaceEditor';
import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { Field, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import StaffEnrollmentWarning from '@/components/StaffEnrollmentWarning';
import StaffNameWithProfile, { hasProfileContent } from '@/components/StaffNameWithProfile';
import StaffProfileHoverCard from '@/components/StaffProfileHoverCard';
import { Color } from '@/constants/color';
import { MIN_CHARS_TO_SEARCH } from '@/constants/search';
import { StaffTypeRequiresEnrollmentSlugs } from '@/constants/staffEnrollmentRequirements';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import usePeople from '@/hooks/usePeople';
import { usePersonPersonRelationshipTypes } from '@/hooks/usePersonPersonRelationships';
import { usePersonStaffRelationshipTypes } from '@/hooks/usePersonStaffRelationships';
import useStaff from '@/hooks/useStaff';
import { useStaffEnrollments } from '@/hooks/useStaffEnrollments';
import PersonContext from '@/pages/Person/PersonContext';
import {
  careTeamEditorPersonPersonSchema,
  careTeamEditorSchema
} from '@/pages/Profile/components/CareTeamSection/careTeamEditorSchema';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import { careTeamSectionSchema } from '@/pages/Profile/schemas/careTeamSectionSchema';
import PersonPersonRelationshipRepository from '@/repositories/PersonPersonRelationshipRepository';
import PersonStaffRelationshipRepository from '@/repositories/PersonStaffRelationshipRepository';
import StaffProfileRepository from '@/repositories/StaffProfileRepository';
import { PersonMinimal } from '@/types/person';
import type { RelatedPerson } from '@/types/personPersonRelationship';
import PersonPersonRelationship from '@/types/personPersonRelationship';
import PersonStaffRelationship from '@/types/personStaffRelationship';
import { StaffProfile } from '@/types/staffProfile';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import DataList, { DataRow } from '@/ui/components/DataList';
import { OptionType } from '@/ui/components/Form/Select/types';
import Heading from '@/ui/components/Heading';
import formatPersonName from '@/utils/formatPersonName';
import formatStaffName from '@/utils/formatStaffName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const formatPersonSupportingInfo = (person: PersonMinimal) => {
  const infoParts: string[] = [];
  if (person.birthday) {
    infoParts.push(`DOB: ${person.birthday}`);
  }

  if (person.jurisdiction_name) {
    infoParts.push(`Jurisdiction: ${person.jurisdiction_name}`);
  }

  return infoParts.join(' | ');
};

const CareTeamSection = () => {
  const queryClient = useQueryClient();
  const { addMessage } = useContext(AppContext);
  const { refetch: refetchPerson, person } = useContext(PersonContext);
  const { navigateToProfileWithId } = useNavigateToPage();
  const {
    staff_relationships,
    person_relationships,
    related_person_relationships,
    insurance_policies
  } = person;
  const personStaffRelationshipRepository = PersonStaffRelationshipRepository();
  const personPersonRelationshipRepository = PersonPersonRelationshipRepository();

  const { data: personStaffRelationshipTypesData } = usePersonStaffRelationshipTypes();

  const personStaffRelationshipTypes = useMemo(() => {
    if (personStaffRelationshipTypesData) {
      const formattedTypes = personStaffRelationshipTypesData.results.map(
        (personStaffRelationshipType) => ({
          label: personStaffRelationshipType.name,
          value: personStaffRelationshipType.id,
          slug: personStaffRelationshipType.slug
        })
      );
      return formattedTypes;
    }
    return [];
  }, [personStaffRelationshipTypesData]);

  const { data: staffEnrollmentsData } = useStaffEnrollments({ person_id: person.id });
  const formatStaffEnrollments = useMemo(() => {
    if (staffEnrollmentsData) {
      const enrollmentsDict = staffEnrollmentsData.reduce(
        (acc: { [key: string]: boolean }, staffEnrollment) => {
          acc[staffEnrollment.staff_id] = true;
          return acc;
        },
        {}
      );
      return enrollmentsDict;
    }
    return {};
  }, [staffEnrollmentsData]);

  const { refetch: getPersonPersonRelationshipTypes } = usePersonPersonRelationshipTypes({}, false);
  const formatPersonPersonRelationshipTypes = useCallback(async () => {
    const { data: personPersonRelationshipTypesData } = await getPersonPersonRelationshipTypes();

    if (personPersonRelationshipTypesData)
      return [
        ...personPersonRelationshipTypesData.results.map((personPersonRelationshipType) => ({
          label: personPersonRelationshipType.name,
          value: personPersonRelationshipType.id
        }))
      ];
    return [];
  }, [getPersonPersonRelationshipTypes]);

  const { data: staffData } = useStaff();
  const formatStaff = useMemo(() => {
    if (staffData?.data)
      return [
        ...staffData.data.map((staff) => ({
          label: formatStaffName({ staff, byLastName: true }),
          value: staff.id
        }))
      ];
    return [];
  }, [staffData]);

  // Fetch staff profiles once so the Staff dropdown can flag (and preview on
  // hover) the staff members who have configured Care Team profile content.
  const staffProfileRepository = StaffProfileRepository();
  const { data: staffProfilesResponse } = useAPI({
    queryKey: ['staffProfiles', 'all'],
    queryFn: () => staffProfileRepository.getStaffProfiles(),
    supressErrorMessages: true
  });

  const staffProfileMap = useMemo(() => {
    const map = new Map<string, StaffProfile>();
    (staffProfilesResponse?.data ?? []).forEach((profile) => {
      if (hasProfileContent(profile)) map.set(profile.staff.id, profile);
    });
    return map;
  }, [staffProfilesResponse]);

  const renderStaffOption = useCallback(
    (option: OptionType) => {
      const profile = staffProfileMap.get(String(option.value));
      if (!profile) return option.label;
      return (
        <StaffProfileHoverCard profile={profile} placement="right-start">
          <span className="inline-flex items-center gap-1">
            <FontAwesomeIcon
              icon={faUser}
              className="text-gray-400 text-xs"
              data-testid="staff-profile-marker"
            />
            {option.label}
          </span>
        </StaffProfileHoverCard>
      );
    },
    [staffProfileMap]
  );

  const [peopleSearchParams, setPeopleSearchParams] = useState({
    my_people: false,
    page: '1',
    has_full_name: true,
    search: ''
  });

  const onPersonSearch = async (search: string) => {
    if (search.length >= MIN_CHARS_TO_SEARCH) {
      setPeopleSearchParams((currentSearchParams) => {
        return { ...currentSearchParams, search };
      });
    }
  };

  const { data: peopleData, isFetching: isFetchingPeople } = usePeople({
    params: peopleSearchParams,
    enabled: !!peopleSearchParams.search
  });

  const formatPersons = useCallback(async () => {
    if (peopleData?.data)
      return [
        ...peopleData.data.results.map((person) => ({
          label: formatPersonName({ person }),
          subLabel: formatPersonSupportingInfo(person),
          value: person.id,
          avatar: person.profile_image_url,
          outlinkUrl: `/agent/people/${person.id}`
        }))
      ];
    return [];
  }, [peopleData?.data]);

  const fields_person: Field<PersonPersonRelationship>[] = [
    {
      alwaysSend: true,
      key: 'person_id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'related_person_id',
      isLoadingOptions: isFetchingPeople,
      label: 'Related Person',
      onSearch: onPersonSearch,
      placeholder: 'Search for a person',
      options: formatPersons,
      type: FieldTypes.SELECT
    },
    {
      key: 'relationship_type_id',
      label: 'Relationship',
      type: FieldTypes.SELECT,
      options: formatPersonPersonRelationshipTypes
    }
  ];

  const fields: Field<PersonStaffRelationship>[] = [
    {
      alwaysSend: true,
      key: 'person_id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      key: 'relationship_type_id',
      label: 'Relationship',
      type: FieldTypes.SELECT,
      options: personStaffRelationshipTypes
    },
    {
      key: 'staff_id',
      label: 'Staff',
      type: FieldTypes.SELECT,
      options: formatStaff,
      formatOptionLabel: renderStaffOption
    },
    {
      alwaysSend: true,
      key: 'relationship_type.id',
      label: '',
      type: FieldTypes.HIDDEN
    },
    {
      alwaysSend: true,
      key: 'staff.id',
      label: '',
      type: FieldTypes.HIDDEN
    }
  ];

  const mutatePersonStaffRelationship = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Partial<PersonStaffRelationship> }) => {
      if (!id) {
        return personStaffRelationshipRepository.createPersonStaffRelationship(values);
      }
      return personStaffRelationshipRepository.updatePersonStaffRelationship(id, values);
    },
    onSuccess: async (response) => {
      if (!response) return;
      await refetchPerson();
      addMessage('success', 'Relationship saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deletePersonStaffRelationShip = useMutation({
    mutationFn: (id: string) => personStaffRelationshipRepository.deletePersonStaffRelationship(id),
    onSuccess: async () => {
      await refetchPerson();
      await queryClient.invalidateQueries({ queryKey: ['getMyPeople'] });
      addMessage('success', 'Relationship deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const mutatePersonPersonRelationship = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Partial<PersonPersonRelationship> }) => {
      if (!id) {
        return personPersonRelationshipRepository.createPersonPersonRelationship(values);
      }
      return personPersonRelationshipRepository.updatePersonPersonRelationship(id, values);
    },
    onSuccess: async (response) => {
      if (!response) return;
      await refetchPerson();
      addMessage('success', 'Relationship saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deletePersonPersonRelationShip = useMutation({
    mutationFn: (id: string) =>
      personPersonRelationshipRepository.deletePersonPersonRelationship(id),
    onSuccess: async () => {
      await refetchPerson();
      addMessage('success', 'Relationship deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const shouldShowStaffEnrollmentWarning = useCallback(
    (staffId: string | undefined, staffEnrollmentTypeId: string | undefined) => {
      if (!staffId || !staffEnrollmentTypeId) return false;

      const relationshipType = personStaffRelationshipTypes.find(
        (type) => type.value === staffEnrollmentTypeId
      );

      const isEnrollmentRequired =
        relationshipType &&
        Object.values(StaffTypeRequiresEnrollmentSlugs).includes(
          relationshipType.slug as StaffTypeRequiresEnrollmentSlugs
        );

      return isEnrollmentRequired && !formatStaffEnrollments[staffId];
    },
    [personStaffRelationshipTypes, formatStaffEnrollments]
  );

  const checkValidationMessage = useCallback(
    (values: Partial<PersonStaffRelationship>) => {
      const staffId = values.staff_id ? values.staff_id : values.staff?.id;
      const relationshipTypeId = values.relationship_type_id
        ? values.relationship_type_id
        : values.relationship_type?.id;

      if (shouldShowStaffEnrollmentWarning(staffId, relationshipTypeId)) {
        if (!staffId) return null;

        const policy = insurance_policies?.find((policy) => policy.is_default);
        const selectedStaff = formatStaff.find((staff) => staff.value === staffId);

        return (
          <StaffEnrollmentWarning
            staffId={staffId}
            personId={person.id}
            staffName={selectedStaff?.label}
            insurance={policy?.provider_name ?? undefined}
            insuranceType={policy?.type ?? undefined}
            jurisdiction={person.jurisdiction?.name ?? undefined}
          />
        );
      }
      return null;
    },
    [shouldShowStaffEnrollmentWarning, insurance_policies, formatStaff, person]
  );

  const renderStaffEnrollmentWarning = (
    staffId: string | undefined,
    staffEnrollmentTypeId: string | undefined
  ) => {
    if (shouldShowStaffEnrollmentWarning(staffId, staffEnrollmentTypeId)) {
      return (
        <span className="inline-block m-1">
          <Badge color={Color.ORANGE} text="Missing credential enrollment" />
        </span>
      );
    }
    return null;
  };

  return (
    <div id="care-team-section" className="relative">
      <SectionHeading text="Member Care" />
      <PersonInPlaceEditor
        schema={careTeamSectionSchema}
        sectionEditor
        fields={[
          {
            key: 'recovery_goal',
            label: 'Recovery Goal',
            type: FieldTypes.TEXTAREA
          }
        ]}>
        <DataList>
          <DataRow label="Recovery Goal" sensitive>
            {person.recovery_goal}
          </DataRow>
        </DataList>
      </PersonInPlaceEditor>

      <hr className="my-4" />

      <section>
        <Heading value={3} text="Staff Relationships" />
        {!staff_relationships.length ? (
          <div data-testid="staff-relationships-none-found">No relationships found.</div>
        ) : (
          <div>
            <DataList>
              {staff_relationships.map((personStaffRelationship) => {
                return (
                  <div key={personStaffRelationship.id} className="group/subsection relative">
                    <InPlaceEditor
                      deleteMutation={deletePersonStaffRelationShip}
                      disableEdit={
                        mutatePersonStaffRelationship.isPending ||
                        deletePersonStaffRelationShip.isPending
                      }
                      fields={fields}
                      mutation={mutatePersonStaffRelationship}
                      record={personStaffRelationship}
                      schema={careTeamEditorSchema}
                      checkValidationMessage={checkValidationMessage}
                      subsectionEditor>
                      <DataRow label={personStaffRelationship.relationship_type?.name}>
                        {personStaffRelationship.staff ? (
                          <div className="inline-flex items-center gap-2">
                            <StaffNameWithProfile staff={personStaffRelationship.staff} />
                            {renderStaffEnrollmentWarning(
                              personStaffRelationship.staff.id,
                              personStaffRelationship.relationship_type?.id
                            )}
                          </div>
                        ) : (
                          <>Staff missing</>
                        )}
                      </DataRow>
                    </InPlaceEditor>
                  </div>
                );
              })}
            </DataList>
          </div>
        )}

        <div className="group/subsection relative mt-3">
          <InPlaceEditor
            fields={fields}
            mutation={mutatePersonStaffRelationship}
            newRecord
            record={{ person_id: person.id }}
            schema={careTeamEditorSchema}
            checkValidationMessage={checkValidationMessage}
          />
        </div>
      </section>

      <hr className="my-4" />

      <section>
        <Heading value={3} text="Person Relationships" />
        {!person_relationships.length ? (
          <div data-testid="person-relationships-none-found">No relationships found.</div>
        ) : (
          <div>
            <DataList>
              {person_relationships.map((personPersonRelationship) => {
                return (
                  <div key={personPersonRelationship.id} className="group/subsection relative">
                    <InPlaceEditor
                      deleteMutation={deletePersonPersonRelationShip}
                      disableEdit={
                        mutatePersonPersonRelationship.isPending ||
                        deletePersonPersonRelationShip.isPending
                      }
                      fields={fields_person}
                      mutation={mutatePersonPersonRelationship}
                      record={personPersonRelationship}
                      schema={careTeamEditorPersonPersonSchema}
                      subsectionEditor>
                      <DataRow label={personPersonRelationship.relationship_type?.name}>
                        {personPersonRelationship.related_person?.id ? (
                          <button
                            className="mp-sensitive text-blue-600 underline"
                            key={person.id}
                            onClick={() =>
                              navigateToProfileWithId(
                                personPersonRelationship.related_person?.id as string
                              )
                            }
                            type="button">
                            {formatPersonName({
                              person: personPersonRelationship.related_person as RelatedPerson
                            })}
                            <FontAwesomeIcon icon={faExternalLink} className="ml-1" />
                          </button>
                        ) : (
                          <>Related Person missing</>
                        )}
                      </DataRow>
                    </InPlaceEditor>
                  </div>
                );
              })}
            </DataList>
          </div>
        )}
        <div className="group/subsection relative mt-3">
          <InPlaceEditor
            fields={fields_person}
            mutation={mutatePersonPersonRelationship}
            newRecord
            record={{ person_id: person.id }}
            schema={careTeamEditorPersonPersonSchema}
          />
        </div>
        <hr className="my-3" />
        <Heading value={5} text="Linked Relationships" />
        {!related_person_relationships.length ? (
          <div data-testid="person-related-relationships-none-found">
            No linked relationships found.
          </div>
        ) : (
          <DataList>
            {related_person_relationships.map((personPersonRelationship) => {
              return (
                <div key={personPersonRelationship.id} className="group/subsection relative mb-1">
                  <DataRow label={`${personPersonRelationship.relationship_type?.name} of`}>
                    {personPersonRelationship.person?.id ? (
                      <>
                        <button
                          className="mp-sensitive text-blue-600 underline"
                          key={person.id}
                          onClick={() =>
                            navigateToProfileWithId(personPersonRelationship.person?.id as string)
                          }
                          type="button">
                          {formatPersonName({
                            person: personPersonRelationship.person as RelatedPerson
                          })}
                          <FontAwesomeIcon icon={faExternalLink} className="ml-1" />
                        </button>
                        <div className="absolute mt-1 top-0 right-0 group-hover/subsection:visible invisible space-x-1">
                          <Button
                            ariaLabel="Delete"
                            color={Color.RED}
                            disabled={false}
                            onClick={() =>
                              confirm('Are you sure you want to delete this record?') &&
                              personPersonRelationship.id
                                ? deletePersonPersonRelationShip.mutate(personPersonRelationship.id)
                                : null
                            }
                            size="xs">
                            <FontAwesomeIcon
                              className={
                                deletePersonPersonRelationShip.isPending ? 'animate-spin' : ''
                              }
                              icon={deletePersonPersonRelationShip.isPending ? faSpinner : faTrash}
                            />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div></div>
                    )}
                  </DataRow>
                </div>
              );
            })}
          </DataList>
        )}
      </section>
    </div>
  );
};

export default memo(CareTeamSection);
